import { useState, useEffect, useCallback, useMemo } from 'react';
import { rrhhApi, trabajadoresApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from '../../hooks/use-toast';
import {
  CalendarIcon, UtensilsCrossed, Users, MapPin, Briefcase,
  ChevronDown, ChevronUp, CheckCircle, Clock, Send, Loader2,
  Settings, Plus, Trash2, Mail, AlertTriangle, Pencil, X
} from 'lucide-react';

const TIPO_COLORS = {
  A: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  H: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  C: 'bg-blue-500/10 text-blue-700 dark:text-blue-300',
};
const TIPO_LABELS = { A: 'Normal', H: 'Hipocalórico', C: 'Colación' };

function formatDate(d) {
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}
function parseDate(str) {
  // Iter 292.g: acepta '/' o '-'.
  const parts = String(str || '').split(/[/-]/);
  if (parts.length === 3) return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
  return new Date();
}

export default function ResumenAlmuerzos() {
  const [fecha, setFecha] = useState(formatDate(new Date()));
  const [calOpen, setCalOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [sendingId, setSendingId] = useState(null);
  const [vista, setVista] = useState('pedidos'); // 'pedidos' | 'listado'
  const [addingCasino, setAddingCasino] = useState(null); // key del casino donde agregar
  const [addTrabNum, setAddTrabNum] = useState('');
  const [addTipo, setAddTipo] = useState('A');
  const [allWorkers, setAllWorkers] = useState([]);

  // Concesionarios config
  const [showConfig, setShowConfig] = useState(false);
  const [concesionarios, setConcesionarios] = useState([]);
  const [newConc, setNewConc] = useState({
    lugar: '', nombre: '', email: '', contacto: '',
    // Iter 236.v — vínculo con Maestro de Proveedores Alegra
    proveedor_alegra_id: '', proveedor_rut: '', proveedor_razon_social: '',
  });
  const [editingConc, setEditingConc] = useState(null); // lugar original que se está editando
  // Iter 236.v — autocomplete proveedor
  const [provSearch, setProvSearch] = useState('');
  const [provResults, setProvResults] = useState([]);
  const [provSearchBusy, setProvSearchBusy] = useState(false);
  const [showProvDropdown, setShowProvDropdown] = useState(false);
  // Iter 236.v — agrupar informe por casino o rut
  const [agruparPor, setAgruparPor] = useState('casino');

  const loadResumen = useCallback(async () => {
    try {
      setLoading(true);
      const res = await rrhhApi.getResumenAlmuerzos({ fecha });
      setData(res.data);
    } catch (err) {
      console.error('[ResumenAlmuerzos] Error al cargar resumen:', err);
      toast({ title: 'Error al cargar resumen', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [fecha]);

  useEffect(() => { loadResumen(); }, [loadResumen]);

  // Load all workers once for the add-dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await trabajadoresApi.getAll({ activo: true });
        setAllWorkers(res.data || []);
      } catch (err) { console.warn('[ResumenAlmuerzos] No se pudo cargar listado de trabajadores activos:', err); }
    })();
  }, []);

  const canEdit = !!data?.can_edit;

  const showConflictToast = (err) => {
    const det = err?.response?.data?.detail;
    if (err?.response?.status === 409 && det?.conflicto) {
      const c = det.conflicto;
      const lugar = c.otro_lugar_almuerzo_otro || c.otro_lugar_almuerzo || '';
      toast({
        title: `${c.trabajador_nombre} ya tiene almuerzo pedido`,
        description: `En OT ${c.otro_ot_principal}${c.otro_ot_descripcion ? ' - ' + c.otro_ot_descripcion : ''}${lugar ? ' · ' + lugar : ''}${c.otro_supervisor ? ' · Supervisor: ' + c.otro_supervisor : ''}. Desmarque primero allá.`,
        variant: 'destructive',
        duration: 8000,
      });
      return true;
    }
    return false;
  };

  const patchTrabajador = async (parteId, trabajadorNumero, almuerzo, tipo = 'A') => {
    try {
      await rrhhApi.patchAlmuerzoTrabajador({
        parte_id: parteId,
        trabajador_numero: trabajadorNumero,
        almuerzo,
        tipo_almuerzo: tipo,
      });
      await loadResumen();
    } catch (err) {
      if (!showConflictToast(err)) {
        toast({
          title: err.response?.data?.detail?.message || err.response?.data?.detail || 'Error al actualizar',
          variant: 'destructive',
        });
      }
    }
  };

  const agregarTrabajador = async (grupo) => {
    if (!addTrabNum) { toast({ title: 'Seleccione un trabajador', variant: 'destructive' }); return; }
    const first = grupo.pedidos?.[0];
    try {
      const res = await rrhhApi.agregarTrabajadorAlmuerzo({
        fecha,
        lugar_almuerzo_raw: first?.lugar_almuerzo_raw || '',
        lugar_almuerzo_otro: first?.lugar_almuerzo_raw === 'otro' ? grupo.casino : '',
        trabajador_numero: parseInt(addTrabNum),
        tipo_almuerzo: addTipo,
      });
      toast({ title: res.data.message });
      setAddingCasino(null);
      setAddTrabNum('');
      setAddTipo('A');
      await loadResumen();
    } catch (err) {
      if (!showConflictToast(err)) {
        toast({
          title: err.response?.data?.detail?.message || err.response?.data?.detail || 'Error al agregar',
          variant: 'destructive',
        });
      }
    }
  };

  const [showInactivos, setShowInactivos] = useState(false);
  const loadConcesionarios = async (incluirInactivos = false) => {
    try {
      const res = await rrhhApi.getConcesionarios({ incluir_inactivos: incluirInactivos });
      setConcesionarios(res.data);
    } catch (err) { console.warn('[ResumenAlmuerzos] No se pudieron cargar concesionarios:', err); }
  };

  const handleSaveConc = async () => {
    if (!newConc.lugar || !newConc.email) { toast({ title: 'Ingrese lugar y email', variant: 'destructive' }); return; }
    try {
      // Si está en modo edición y cambió el lugar, primero hay que borrar el antiguo
      if (editingConc && editingConc !== newConc.lugar) {
        try { await rrhhApi.deleteConcesionario(editingConc); } catch (err) { console.warn('[ResumenAlmuerzos] Falló delete previo a upsert (esperado si no existía):', err); }
      }
      await rrhhApi.upsertConcesionario(newConc);
      toast({ title: `Concesionario '${newConc.nombre || newConc.lugar}' guardado` });
      setNewConc({
        lugar: '', nombre: '', email: '', contacto: '',
        proveedor_alegra_id: '', proveedor_rut: '', proveedor_razon_social: '',
      });
      setEditingConc(null);
      setProvSearch('');
      setProvResults([]);
      loadConcesionarios(showInactivos);
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const handleEditConc = (c) => {
    setNewConc({
      lugar: c.lugar || '',
      nombre: c.nombre || '',
      email: c.email || '',
      contacto: c.contacto || '',
      proveedor_alegra_id: c.proveedor_alegra_id || '',
      proveedor_rut: c.proveedor_rut || '',
      proveedor_razon_social: c.proveedor_razon_social || '',
    });
    setEditingConc(c.lugar);
    setProvSearch(c.proveedor_razon_social ? `${c.proveedor_rut || ''} · ${c.proveedor_razon_social}` : '');
    setProvResults([]);
    setShowProvDropdown(false);
  };

  const cancelEdit = () => {
    setNewConc({
      lugar: '', nombre: '', email: '', contacto: '',
      proveedor_alegra_id: '', proveedor_rut: '', proveedor_razon_social: '',
    });
    setEditingConc(null);
    setProvSearch('');
    setProvResults([]);
  };

  // Iter 236.v — buscar proveedor Alegra por RUT o razón social
  const buscarProveedor = async (q) => {
    setProvSearch(q);
    if ((q || '').trim().length < 2) {
      setProvResults([]);
      setShowProvDropdown(false);
      return;
    }
    setProvSearchBusy(true);
    setShowProvDropdown(true);
    try {
      const r = await rrhhApi.searchProveedoresConcesionario(q);
      setProvResults(r.data?.items || []);
    } catch (err) {
      console.warn('[ResumenAlmuerzos] Error buscando proveedores:', err);
      setProvResults([]);
    } finally {
      setProvSearchBusy(false);
    }
  };

  const seleccionarProveedor = (p) => {
    setNewConc(prev => ({
      ...prev,
      proveedor_alegra_id: p.alegra_id,
      proveedor_rut: p.rut,
      proveedor_razon_social: p.razon_social,
    }));
    setProvSearch(`${p.rut} · ${p.razon_social}`);
    setShowProvDropdown(false);
  };

  const desvincularProveedor = () => {
    setNewConc(prev => ({
      ...prev,
      proveedor_alegra_id: '',
      proveedor_rut: '',
      proveedor_razon_social: '',
    }));
    setProvSearch('');
    setProvResults([]);
  };

  const handleDeleteConc = async (lugar) => {
    if (!window.confirm(`¿Bloquear y ocultar el concesionario "${lugar}"? No se eliminarán partes pero no podrán enviarse correos a este lugar.`)) return;
    try {
      await rrhhApi.deleteConcesionario(lugar);
      toast({ title: 'Concesionario bloqueado y oculto' });
      loadConcesionarios(showInactivos);
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const handleReactivarConc = async (lugar) => {
    try {
      await rrhhApi.reactivarConcesionario(lugar);
      toast({ title: 'Concesionario reactivado' });
      loadConcesionarios(showInactivos);
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const handleEnviarPedido = async (parteId) => {
    setSendingId(parteId);
    try {
      const res = await rrhhApi.enviarResumenConsolidado({ parte_id: parteId });
      toast({ title: res.data.message, duration: 6000 });
      loadResumen();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error al enviar', variant: 'destructive', duration: 8000 });
    } finally {
      setSendingId(null);
    }
  };

  const handleEliminarPedido = async (p) => {
    const txt = `¿Eliminar el pedido OT ${p.ot_principal}${p.ot_descripcion ? ' - ' + p.ot_descripcion : ''} (${p.total_almuerzos} almuerzos)?\n\nEsto borrará el Parte Diario completo y sus HH asociadas.`;
    if (!window.confirm(txt)) return;
    try {
      await rrhhApi.eliminarParte(p.parte_id);
      toast({ title: 'Pedido eliminado' });
      loadResumen();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error al eliminar', variant: 'destructive' });
    }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  // Agrupar pedidos por casino/lugar de almuerzo y calcular totales por sección
  const pedidosPorCasino = useMemo(() => {
    const grupos = {};
    for (const p of (data?.pedidos || [])) {
      const key = (p.lugar_almuerzo || 'Sin lugar asignado').trim();
      if (!grupos[key]) {
        grupos[key] = {
          casino: key,
          email: p.concesionario_email || '',
          nombre: p.concesionario_nombre || '',
          pedidos: [],
          total_almuerzos: 0,
          total_presentes: 0,
          tipos: { A: 0, H: 0, C: 0 },
          pendientes: 0,
        };
      }
      const g = grupos[key];
      g.pedidos.push(p);
      g.total_almuerzos += (p.total_almuerzos || 0);
      g.total_presentes += (p.total_presentes || 0);
      for (const t of ['A', 'H', 'C']) g.tipos[t] += (p.tipo_counts?.[t] || 0);
      if (!p.almuerzo_enviado && (p.total_almuerzos || 0) > 0) g.pendientes += 1;
      if (!g.email && p.concesionario_email) { g.email = p.concesionario_email; g.nombre = p.concesionario_nombre; }
    }
    // Ordenar: primero los que tienen pendientes, luego alfabético
    return Object.values(grupos).sort((a, b) => {
      if ((b.pendientes > 0) !== (a.pendientes > 0)) return (b.pendientes > 0) - (a.pendientes > 0);
      return a.casino.localeCompare(b.casino, 'es');
    });
  }, [data]);

  // Detectar trabajadores con almuerzo en más de un parte el mismo día
  const duplicados = useMemo(() => {
    const map = new Map(); // trabajador_numero -> { nombre, entries: Map<"ot|lugar", {ot,lugar,count}> }
    for (const p of (data?.pedidos || [])) {
      const lugar = p.lugar_almuerzo || 'Sin lugar';
      const ot = p.ot_principal;
      for (const t of (p.detalle || [])) {
        if (!t.almuerzo) continue;
        const key = t.trabajador_numero;
        if (!map.has(key)) map.set(key, { nombre: t.nombre || '', entries: new Map() });
        const entry = map.get(key);
        const ek = `${ot}|${lugar}`;
        if (!entry.entries.has(ek)) entry.entries.set(ek, { ot, lugar, count: 0 });
        entry.entries.get(ek).count += 1;
      }
    }
    return Array.from(map.entries())
      .filter(([, v]) => v.entries.size > 1) // >1 combinación OT+lugar distinta
      .map(([num, v]) => ({
        trabajador_numero: num,
        nombre: v.nombre,
        entries: Array.from(v.entries.values()),
      }));
  }, [data]);

  if (loading && !data) return <TableSkeleton rows={5} cols={6} />;

  const d = data || { fecha: '', total_pedidos: 0, total_almuerzos: 0, total_presentes: 0, total_por_tipo: {}, pedidos: [] };
  const tpt = d.total_por_tipo || {};

  return (
    <div className="space-y-4 page-enter" data-testid="resumen-almuerzos">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Resumen Almuerzos</h1>
          <p className="text-sm text-muted-foreground">Envíe solicitudes por pedido a cada concesionario</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border overflow-hidden" data-testid="vista-toggle">
            {[
              { k: 'pedidos', label: 'Pedidos' },
              { k: 'listado', label: 'Listado' },
            ].map(v => (
              <button key={v.k} onClick={() => setVista(v.k)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${vista === v.k ? 'bg-primary text-white' : 'bg-background hover:bg-muted'}`}
                data-testid={`btn-vista-${v.k}`}>
                {v.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" className="rounded-xl gap-1.5 text-xs"
            onClick={() => { setShowConfig(true); loadConcesionarios(); }} data-testid="btn-config-conc">
            <Settings className="h-3.5 w-3.5" /> Concesionarios
          </Button>
          <Input value={fecha} onChange={(e) => setFecha(e.target.value)} placeholder="DD/MM/AAAA" className="rounded-xl w-36" data-testid="input-fecha-resumen" />
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="rounded-xl"><CalendarIcon className="h-4 w-4" /></Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={parseDate(fecha)} onSelect={(day) => { if (day) { setFecha(formatDate(day)); setCalOpen(false); } }} />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
        <Card><CardContent className="p-3 flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-primary/10"><Briefcase className="h-3.5 w-3.5 text-primary" /></div>
          <div><p className="text-[9px] text-muted-foreground">Partes</p><p className="text-sm font-bold">{d.total_pedidos}</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-3 flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-amber-500/10"><UtensilsCrossed className="h-3.5 w-3.5 text-amber-600" /></div>
          <div><p className="text-[9px] text-muted-foreground">Almuerzos</p><p className="text-sm font-bold">{d.total_almuerzos}</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-3 flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-500/10"><Users className="h-3.5 w-3.5 text-blue-600" /></div>
          <div><p className="text-[9px] text-muted-foreground">Presentes</p><p className="text-sm font-bold">{d.total_presentes}</p></div>
        </CardContent></Card>
        {['A', 'H', 'C'].map(t => (
          <Card key={t}><CardContent className="p-3 flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${TIPO_COLORS[t].split(' ')[0]}`}>
              <span className={`text-[10px] font-bold ${TIPO_COLORS[t].split(' ').slice(1).join(' ')}`}>{t}</span>
            </div>
            <div><p className="text-[9px] text-muted-foreground">{TIPO_LABELS[t]}</p><p className="text-sm font-bold">{tpt[t] || 0}</p></div>
          </CardContent></Card>
        ))}
        <Card><CardContent className="p-3 flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-emerald-500/10"><MapPin className="h-3.5 w-3.5 text-emerald-600" /></div>
          <div><p className="text-[9px] text-muted-foreground">Lugares</p><p className="text-sm font-bold">{new Set(d.pedidos.map(p => p.lugar_almuerzo).filter(Boolean)).size || '-'}</p></div>
        </CardContent></Card>
      </div>

      {/* Banner de duplicados: trabajadores con almuerzo en más de un parte */}
      {duplicados.length > 0 && (
        <Card className="border-red-400 bg-red-50/60 dark:bg-red-950/30" data-testid="banner-duplicados-almuerzo">
          <CardContent className="p-3 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                {duplicados.length} trabajador(es) con almuerzo duplicado en varios partes
              </p>
              <ul className="mt-1 text-xs text-red-700/90 dark:text-red-300/90 space-y-0.5">
                {duplicados.slice(0, 10).map(d => (
                  <li key={d.trabajador_numero}>
                    <span className="font-medium">{d.nombre}</span>:
                    {' '}{d.entries.map((e, i) => (
                      <span key={`${d.trabajador_numero}-${e.ot}-${e.lugar}`}>
                        {i > 0 ? ' ↔ ' : ''}OT {e.ot} ({e.lugar}){e.count > 1 ? ` ×${e.count}` : ''}
                      </span>
                    ))}
                  </li>
                ))}
                {duplicados.length > 10 && (
                  <li className="italic">+{duplicados.length - 10} más…</li>
                )}
              </ul>
              <p className="text-[11px] text-red-600/80 dark:text-red-300/70 mt-1">
                Corrija desmarcando el almuerzo en el parte incorrecto. Un trabajador solo puede pedir almuerzo en UN parte por día.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pedidos agrupados por Casino */}
      {vista === 'pedidos' && (
      d.pedidos.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No hay pedidos para {fecha}</CardContent></Card>
      ) : (
        <div className="space-y-6" data-testid="casino-sections-container">
          {pedidosPorCasino.map((grupo) => (
            <section key={grupo.casino} data-testid={`casino-${grupo.casino.replace(/\s+/g, '-')}`}>
              {/* Header del casino */}
              <div className="sticky top-0 z-10 -mx-1 px-3 py-2 mb-2 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 flex flex-wrap items-center gap-2 backdrop-blur-sm">
                <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                <h2 className="text-base font-bold tracking-tight">{grupo.casino}</h2>
                {grupo.email ? (
                  <span className="flex items-center gap-1 text-xs bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded text-emerald-700 dark:text-emerald-400">
                    <Mail className="h-3 w-3" /> {grupo.nombre || grupo.email}
                  </span>
                ) : (
                  <span className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded">Sin concesionario</span>
                )}
                <div className="flex-1" />
                <span className="text-xs text-muted-foreground">{grupo.pedidos.length} {grupo.pedidos.length === 1 ? 'parte' : 'partes'}</span>
                <span className="text-xs text-muted-foreground">|</span>
                <span className="text-xs"><Users className="h-3 w-3 inline" /> {grupo.total_presentes}</span>
                {grupo.tipos.A > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.A}`}>A:{grupo.tipos.A}</span>}
                {grupo.tipos.H > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.H}`}>H:{grupo.tipos.H}</span>}
                {grupo.tipos.C > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.C}`}>C:{grupo.tipos.C}</span>}
                <span className="text-base font-bold tabular-nums text-primary ml-1" data-testid={`total-casino-${grupo.casino.replace(/\s+/g, '-')}`}>{grupo.total_almuerzos}</span>
                {grupo.pendientes > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-300">
                    {grupo.pendientes} pend.
                  </span>
                )}
              </div>

              {/* Pedidos del casino */}
              <div className="space-y-2 pl-1">
                {grupo.pedidos.map((p) => (
            <Card key={p.parte_id} className={`transition-all ${p.almuerzo_enviado ? 'border-emerald-500/30' : ''}`} data-testid={`pedido-${p.parte_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    {p.almuerzo_enviado ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : <Clock className="h-4 w-4 text-amber-600" />}
                    <span className="font-semibold">OT {p.ot_principal}</span>
                    {p.ot_descripcion && <span className="text-sm text-muted-foreground">- {p.ot_descripcion.substring(0, 35)}</span>}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.almuerzo_enviado ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}`}>
                    {p.almuerzo_enviado ? 'Enviado' : 'Pendiente'}
                  </span>
                  {p.tipo_counts?.A > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.A}`}>A:{p.tipo_counts.A}</span>}
                  {p.tipo_counts?.H > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.H}`}>H:{p.tipo_counts.H}</span>}
                  {p.tipo_counts?.C > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.C}`}>C:{p.tipo_counts.C}</span>}
                  <div className="flex-1" />
                  <span className="text-lg font-bold tabular-nums text-primary">{p.total_almuerzos}</span>
                  {/* Send button per pedido */}
                  <Button size="sm" disabled={sendingId === p.parte_id || p.total_almuerzos === 0}
                    onClick={() => handleEnviarPedido(p.parte_id)}
                    className={`rounded-xl gap-1 text-xs ${p.almuerzo_enviado ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'} text-white`}
                    data-testid={`btn-enviar-${p.parte_id}`}>
                    {sendingId === p.parte_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    {p.almuerzo_enviado ? 'Reenviar' : 'Enviar'}
                  </Button>
                  {canEdit && (
                    <Button size="sm" variant="outline" title="Eliminar pedido"
                      onClick={() => handleEliminarPedido(p)}
                      className="rounded-xl text-xs text-red-600 border-red-300 hover:bg-red-50"
                      data-testid={`btn-eliminar-${p.parte_id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {p.total_presentes} presentes</span>
                  {p.almuerzo_enviado_at && <span className="text-[10px] text-emerald-600">Enviado: {p.almuerzo_enviado_at.substring(0, 16).replace('T', ' ')}</span>}
                </div>

                <button onClick={() => toggleExpand(p.parte_id)}
                  className="flex items-center gap-1 text-xs text-primary mt-2 hover:underline">
                  {expanded[p.parte_id] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  {expanded[p.parte_id] ? 'Ocultar' : 'Ver detalle'}
                </button>

                {expanded[p.parte_id] && (
                  <div className="mt-2 rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>Trabajador</TableHead>
                        <TableHead className="w-20 text-center">Almuerzo</TableHead>
                        <TableHead className="w-28 text-center">Tipo</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {(p.detalle || []).map((t, i) => (
                          <TableRow key={t.trabajador_numero || `d-${i}`} className={!t.almuerzo ? 'opacity-50' : ''}>
                            <TableCell className="text-xs">{i + 1}</TableCell>
                            <TableCell className="text-sm">{t.nombre}</TableCell>
                            <TableCell className="text-center">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${t.almuerzo ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                                {t.almuerzo ? 'SI' : 'NO'}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              {t.almuerzo && t.tipo_almuerzo && (
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${TIPO_COLORS[t.tipo_almuerzo] || TIPO_COLORS.A}`}>
                                  {t.tipo_almuerzo} - {TIPO_LABELS[t.tipo_almuerzo] || 'Normal'}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      )
      )}

      {/* LISTADO editable tipo Panel Personal */}
      {vista === 'listado' && (
        d.pedidos.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No hay partes para {fecha}</CardContent></Card>
        ) : (
          <div className="space-y-4" data-testid="listado-container">
            {!canEdit && (
              <Card className="border-amber-400 bg-amber-50/60 dark:bg-amber-950/30">
                <CardContent className="p-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Solo lectura. Requiere rol administrador o encargado RRHH para editar.
                  </p>
                </CardContent>
              </Card>
            )}
            {pedidosPorCasino.map((grupo) => {
              const allTrabs = grupo.pedidos.flatMap(p => (p.detalle || []));
              const conAlmuerzo = allTrabs.filter(t => t.almuerzo).length;
              const total = allTrabs.length;
              return (
                <section key={`listado-${grupo.casino}`} data-testid={`listado-casino-${grupo.casino.replace(/\s+/g, '-')}`}>
                  <div className="sticky top-0 z-10 px-3 py-2 mb-2 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 flex flex-wrap items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    <h2 className="text-base font-bold">{grupo.casino}</h2>
                    {grupo.email ? (
                      <span className="text-[10px] bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded text-emerald-700 dark:text-emerald-400">
                        {grupo.nombre || grupo.email}
                      </span>
                    ) : (
                      <span className="text-[10px] bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded text-red-600">Sin email</span>
                    )}
                    <div className="flex-1" />
                    <span className="text-xs text-muted-foreground">{conAlmuerzo}/{total} con almuerzo</span>
                    {grupo.tipos.A > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.A}`}>A:{grupo.tipos.A}</span>}
                    {grupo.tipos.H > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.H}`}>H:{grupo.tipos.H}</span>}
                    {grupo.tipos.C > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.C}`}>C:{grupo.tipos.C}</span>}
                    {canEdit && (
                      <Button size="sm" variant="outline" className="rounded-xl text-xs gap-1 h-7"
                        onClick={() => setAddingCasino(addingCasino === grupo.casino ? null : grupo.casino)}
                        data-testid={`btn-add-casino-${grupo.casino.replace(/\s+/g, '-')}`}
                      >
                        <Plus className="h-3 w-3" /> Agregar
                      </Button>
                    )}
                  </div>

                  {addingCasino === grupo.casino && canEdit && (
                    <div className="mb-2 p-3 rounded-lg border bg-muted/30 flex flex-wrap items-end gap-2">
                      <div className="flex-1 min-w-[220px]">
                        <label className="text-[10px] text-muted-foreground block mb-1">Trabajador</label>
                        <select value={addTrabNum} onChange={e => setAddTrabNum(e.target.value)}
                          className="h-9 w-full rounded-lg border bg-background px-2 text-sm"
                          data-testid="select-add-trabajador">
                          <option value="">-- Seleccione --</option>
                          {allWorkers.map(w => (
                            <option key={w.numero} value={w.numero}>{w.numero} - {w.nombre}</option>
                          ))}
                        </select>
                      </div>
                      <div className="w-32">
                        <label className="text-[10px] text-muted-foreground block mb-1">Tipo</label>
                        <select value={addTipo} onChange={e => setAddTipo(e.target.value)}
                          className="h-9 w-full rounded-lg border bg-background px-2 text-sm">
                          <option value="A">A - Normal</option>
                          <option value="H">H - Hipocalórico</option>
                          <option value="C">C - Colación</option>
                        </select>
                      </div>
                      <Button size="sm" onClick={() => agregarTrabajador(grupo)} className="rounded-xl gap-1 h-9" data-testid="btn-confirm-add">
                        <Plus className="h-3.5 w-3.5" /> Agregar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setAddingCasino(null); setAddTrabNum(''); }}>Cancelar</Button>
                    </div>
                  )}

                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>Trabajador</TableHead>
                        <TableHead className="w-24">OT</TableHead>
                        <TableHead className="w-40">Supervisor</TableHead>
                        <TableHead className="w-32 text-center">Tipo almuerzo</TableHead>
                        {canEdit && <TableHead className="w-16 text-center">Acción</TableHead>}
                      </TableRow></TableHeader>
                      <TableBody>
                        {allTrabs.map((t, i) => (
                          <TableRow key={`${t.parte_id}-${t.trabajador_numero}`} className={!t.almuerzo ? 'opacity-60' : ''}>
                            <TableCell className="text-xs">{i + 1}</TableCell>
                            <TableCell className="text-sm font-medium">{t.nombre}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">OT {t.ot_principal}</TableCell>
                            <TableCell className="text-xs text-muted-foreground truncate">{t.supervisor_nombre || '-'}</TableCell>
                            <TableCell className="text-center">
                              {canEdit ? (
                                <select
                                  value={t.almuerzo ? (t.tipo_almuerzo || 'A') : ''}
                                  onChange={e => {
                                    const v = e.target.value;
                                    patchTrabajador(t.parte_id, t.trabajador_numero, !!v, v || 'A');
                                  }}
                                  className="h-7 rounded border bg-background px-1 text-xs"
                                  data-testid={`sel-almuerzo-${t.trabajador_numero}`}
                                >
                                  <option value="">No</option>
                                  <option value="A">A - Normal</option>
                                  <option value="H">H - Hipocalórico</option>
                                  <option value="C">C - Colación</option>
                                </select>
                              ) : (
                                t.almuerzo && t.tipo_almuerzo ? (
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${TIPO_COLORS[t.tipo_almuerzo] || TIPO_COLORS.A}`}>
                                    {t.tipo_almuerzo} - {TIPO_LABELS[t.tipo_almuerzo] || 'Normal'}
                                  </span>
                                ) : <span className="text-xs text-muted-foreground">No</span>
                              )}
                            </TableCell>
                            {canEdit && (
                              <TableCell className="text-center">
                                {t.almuerzo && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500"
                                    title="Quitar almuerzo"
                                    onClick={() => patchTrabajador(t.parte_id, t.trabajador_numero, false, '')}
                                    data-testid={`btn-remove-almuerzo-${t.trabajador_numero}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </section>
              );
            })}
          </div>
        )
      )}

      {/* Concesionarios Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Concesionarios</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-muted-foreground flex-1">Configure el email de cada concesionario. El lugar debe coincidir con el nombre del lugar de almuerzo (ej: "Casino Mapal", "Sitio Escuadrón"). Al eliminar, se bloquea y oculta (no se borran datos).</p>
            <label className="flex items-center gap-1 text-[11px] text-muted-foreground cursor-pointer ml-2 whitespace-nowrap">
              <input type="checkbox" checked={showInactivos}
                onChange={e => { setShowInactivos(e.target.checked); loadConcesionarios(e.target.checked); }}
                data-testid="chk-mostrar-inactivos" />
              Ver bloqueados
            </label>
          </div>
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {concesionarios.map((c) => (
              <div key={c.lugar} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${c.activo === false ? 'bg-red-50/40 dark:bg-red-950/20 opacity-70' : 'bg-muted/30'}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {c.nombre || c.lugar}
                    {c.activo === false && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-700">Bloqueado</span>}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                  {c.proveedor_rut && (
                    <p className="text-[11px] text-blue-700 truncate flex items-center gap-1 mt-0.5"
                       data-testid={`conc-proveedor-badge-${c.lugar}`}>
                      <span className="inline-block px-1 py-0.5 rounded bg-blue-50 border border-blue-200">
                        {c.proveedor_rut}
                      </span>
                      <span className="truncate">{c.proveedor_razon_social}</span>
                    </p>
                  )}
                </div>
                {c.activo === false ? (
                  <Button variant="outline" size="sm" className="h-7 text-xs"
                    onClick={() => handleReactivarConc(c.lugar)} data-testid={`btn-reactivar-${c.lugar}`}>
                    Reactivar
                  </Button>
                ) : (
                  <>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50 shrink-0"
                      title="Editar"
                      onClick={() => handleEditConc(c)} data-testid={`btn-edit-conc-${c.lugar}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 shrink-0"
                      title="Bloquear y ocultar"
                      onClick={() => handleDeleteConc(c.lugar)} data-testid={`btn-delete-conc-${c.lugar}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
            ))}
            {concesionarios.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Sin concesionarios {showInactivos ? '' : 'activos'}</p>}
          </div>
          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium">{editingConc ? `Editando: ${editingConc}` : 'Agregar concesionario'}</p>
              {editingConc && (
                <Button variant="ghost" size="sm" onClick={cancelEdit} className="h-7 text-xs gap-1" data-testid="btn-cancel-edit-conc">
                  <X className="h-3 w-3" /> Cancelar
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Lugar (ej: Casino Mapal)" value={newConc.lugar} onChange={e => setNewConc(p => ({ ...p, lugar: e.target.value }))} className="text-sm" />
              <Input placeholder="Nombre concesionario" value={newConc.nombre} onChange={e => setNewConc(p => ({ ...p, nombre: e.target.value }))} className="text-sm" />
              <Input placeholder="Email" type="email" value={newConc.email} onChange={e => setNewConc(p => ({ ...p, email: e.target.value }))} className="text-sm" />
              <Input placeholder="Contacto (opcional)" value={newConc.contacto} onChange={e => setNewConc(p => ({ ...p, contacto: e.target.value }))} className="text-sm" />
            </div>
            {/* Iter 236.v — SearchCombobox Proveedor Alegra */}
            <div className="relative">
              <label className="text-[11px] text-muted-foreground">Proveedor asociado (Alegra)</label>
              <div className="flex items-center gap-1">
                <Input
                  className="text-sm"
                  placeholder="Buscar por RUT o razón social…"
                  value={provSearch}
                  onChange={e => buscarProveedor(e.target.value)}
                  onFocus={() => provResults.length > 0 && setShowProvDropdown(true)}
                  onBlur={() => setTimeout(() => setShowProvDropdown(false), 200)}
                  data-testid="conc-proveedor-search"
                />
                {newConc.proveedor_rut && (
                  <Button type="button" variant="ghost" size="sm"
                    onClick={desvincularProveedor}
                    className="h-7 text-xs shrink-0"
                    data-testid="conc-proveedor-desvincular"
                    title="Desvincular proveedor">
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              {newConc.proveedor_rut && !showProvDropdown && (
                <p className="text-[11px] text-blue-700 mt-0.5">
                  ✓ Vinculado: {newConc.proveedor_rut} · {newConc.proveedor_razon_social}
                </p>
              )}
              {showProvDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 border rounded shadow-lg max-h-60 overflow-y-auto z-50">
                  {provSearchBusy ? (
                    <p className="text-xs text-muted-foreground px-3 py-2">Buscando…</p>
                  ) : provResults.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-3 py-2">Sin resultados</p>
                  ) : (
                    provResults.map(p => (
                      <button key={p.alegra_id}
                        type="button"
                        onMouseDown={e => { e.preventDefault(); seleccionarProveedor(p); }}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b last:border-b-0"
                        data-testid={`conc-proveedor-opt-${p.alegra_id}`}>
                        <div className="font-mono text-[11px]">{p.rut || '(sin RUT)'}</div>
                        <div className="text-muted-foreground truncate">{p.razon_social}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <Button size="sm" className="rounded-xl gap-1.5" onClick={handleSaveConc} data-testid="btn-save-conc">
              {editingConc ? <><Pencil className="h-3.5 w-3.5" /> Actualizar</> : <><Plus className="h-3.5 w-3.5" /> Guardar</>}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
